import { isWorkspaceLink } from '@/lib/workspace-links';

export interface CitationTarget {
  endpoint: string;
  href: string;
}

// Only server-shaped internal citation links become previews. Ordinary and
// legacy links retain browser behavior; no external URL is fetched by this UI.
export function citationTarget(href?: string, base?: string): CitationTarget | null {
  if (!href || !isWorkspaceLink(href, base)) return null;
  const url = new URL(href, base ?? window.location.href);
  const [path, query] = url.hash.slice(1).split('?');
  const params = new URLSearchParams(query);
  const kind = params.get('canvas');
  const id = params.get('id');
  const start = params.get('start');
  const end = params.get('end');
  const digest = params.get('citation');
  if (
    !['file', 'source', 'page'].includes(kind ?? '') ||
    !id ||
    id.length > 128 ||
    !/^\d+$/.test(start ?? '') ||
    !/^\d+$/.test(end ?? '') ||
    !/^[a-f0-9]{64}$/.test(digest ?? '') ||
    Number(end) <= Number(start) ||
    Number(end) - Number(start) > 24000 ||
    Number(end) > 500_000_000
  )
    return null;
  if (path !== (kind === 'file' ? '/workspace/files' : '/workspace/knowledge')) return null;
  for (const key of params.keys()) {
    if (
      !['canvas', 'id', 'section', 'revision', 'start', 'end', 'citation'].includes(key) ||
      params.getAll(key).length !== 1
    )
      return null;
  }
  const revision = params.get('revision');
  if (kind === 'page' ? !revision || revision.length > 128 : !!revision) return null;
  const proof = new URLSearchParams({ start: start!, end: end!, citation: digest! });
  if (revision) proof.set('revision', revision);
  const resource = kind === 'page' ? 'knowledge/pages' : `${kind}s`;
  return {
    endpoint: `/api/v1/company/${resource}/${encodeURIComponent(id)}/citation?${proof}`,
    href,
  };
}

export interface CitationExcerpt {
  kind: 'file' | 'source' | 'page';
  title: string;
  text: string;
  date: string | null;
  sourceUrl?: string | null;
  coverage?: string | null;
  revision?: number;
  pages?: { first: number; last: number; total: number };
  referenceUrl: string;
}
